// ===========================================
// Business Card Import Page
// ===========================================
// Wizard-style page for batch importing business cards
// using AI recognition via Gemini.

import React, { useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Upload,
  Camera,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  RotateCcw,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useSalesReps } from '@/hooks/useUsers';
import { useAppConfig } from '@/hooks/useAppConfig';
import { useBusinessCardImport } from '@/hooks/useBusinessCardImport';
import { BusinessCardReviewCard } from '@/components/crm/BusinessCardReviewCard';

// Default company types fallback
const DEFAULT_COMPANY_TYPES = [
  'Insurance Agent', 'Plumber', 'Restoration Company', 'Property Manager',
  'Real Estate Agent', 'Contractor', 'Adjuster', 'Attorney', 'Other',
];

type Step = 'setup' | 'review' | 'importing' | 'complete';

export function BusinessCardImportPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: salesReps } = useSalesReps();
  const { data: appConfig } = useAppConfig();
  const companyTypes = appConfig?.companyTypes || DEFAULT_COMPANY_TYPES;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>('setup');
  const [isDragging, setIsDragging] = useState(false);

  const {
    cards,
    sessionSalesRepId,
    sessionSalesRepName,
    setSessionSalesRepId,
    setSessionSalesRepName,
    isProcessing,
    isImporting,
    importResult,
    addFiles,
    processAllCards,
    updateCardData,
    approveCard,
    skipCard,
    approveAll,
    setImportAction,
    importApprovedCards,
    resetSession,
  } = useBusinessCardImport(companyTypes);

  // Default sales rep to logged-in user
  React.useEffect(() => {
    if (user && salesReps && salesReps.length > 0 && !sessionSalesRepId) {
      const currentUserAsRep = salesReps.find((rep) => rep.id === user.id);
      const defaultRep = currentUserAsRep || salesReps.find((rep) => rep.role === 'owner');
      if (defaultRep) {
        setSessionSalesRepId(defaultRep.id);
        setSessionSalesRepName(defaultRep.displayName);
      }
    }
  }, [user, salesReps, sessionSalesRepId, setSessionSalesRepId, setSessionSalesRepName]);

  const handleSalesRepChange = (repId: string) => {
    setSessionSalesRepId(repId);
    const rep = salesReps?.find((r) => r.id === repId);
    setSessionSalesRepName(rep?.displayName || '');
  };

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    addFiles(files);
    // Reset so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      // Filter to only image files
      const imageFiles = Array.from(files).filter((f) => f.type.startsWith('image/'));
      if (imageFiles.length > 0) {
        addFiles(imageFiles);
      } else {
        toast.error('Please drop image files only (JPEG, PNG, WebP)');
      }
    }
  }, [addFiles]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleStartProcessing = async () => {
    if (cards.length === 0) {
      toast.error('Please upload at least one business card photo');
      return;
    }
    if (!sessionSalesRepId) {
      toast.error('Please select a sales rep');
      return;
    }
    setStep('review');
    await processAllCards();
  };

  const handleImport = async () => {
    if (!user) return;
    const approved = cards.filter((c) => c.status === 'approved');
    if (approved.length === 0) {
      toast.error('No cards approved for import');
      return;
    }
    setStep('importing');
    const result = await importApprovedCards(user.id);
    setStep('complete');
    if (result.errors > 0) {
      toast.error(`Import completed with ${result.errors} error(s)`);
    } else {
      toast.success(`Successfully imported ${result.companiesCreated} companies and ${result.contactsAdded} contacts`);
    }
  };

  const handleReset = () => {
    resetSession();
    setStep('setup');
  };

  const parsedCount = cards.filter((c) => c.status === 'parsed').length;
  const approvedCount = cards.filter((c) => c.status === 'approved').length;
  const processingCount = cards.filter((c) => c.status === 'processing').length;
  const pendingCount = cards.filter((c) => c.status === 'pending').length;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Back button */}
      <Link
        to="/companies"
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft size={20} />
        Back to Companies
      </Link>

      {/* Header */}
      <div className="card">
        <div className="flex items-center gap-3 mb-2">
          <Camera size={24} className="text-blue-600" />
          <h1 className="text-xl font-bold text-gray-900">Import Business Cards</h1>
        </div>
        <p className="text-sm text-gray-600">
          Upload photos of business cards and AI will extract the data. Review each card before importing.
        </p>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mt-4 text-sm">
          <StepBadge label="1. Upload" active={step === 'setup'} done={step !== 'setup'} />
          <ChevronRight size={14} className="text-gray-300" />
          <StepBadge label="2. Review" active={step === 'review'} done={step === 'importing' || step === 'complete'} />
          <ChevronRight size={14} className="text-gray-300" />
          <StepBadge label="3. Import" active={step === 'importing'} done={step === 'complete'} />
          <ChevronRight size={14} className="text-gray-300" />
          <StepBadge label="4. Done" active={step === 'complete'} done={false} />
        </div>
      </div>

      {/* Step 1: Setup */}
      {step === 'setup' && (
        <div className="card space-y-6">
          {/* Sales Rep */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sales Rep for this import session
            </label>
            <select
              className="input max-w-sm"
              value={sessionSalesRepId}
              onChange={(e) => handleSalesRepChange(e.target.value)}
            >
              <option value="">Select a sales rep...</option>
              {salesReps?.map((rep) => (
                <option key={rep.id} value={rep.id}>
                  {rep.displayName} ({rep.role})
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              This sales rep will be assigned to all companies imported in this session.
            </p>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Business Card Photos
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              multiple
              style={{ position: 'absolute', width: 0, height: 0, opacity: 0, overflow: 'hidden' }}
              onChange={handleFilesSelected}
            />
            <div
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragging
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
              }`}
            >
              <Upload size={32} className={`mx-auto mb-3 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
              <p className="text-sm font-medium text-gray-700">
                {isDragging ? 'Drop images here' : 'Click to upload or drag & drop'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                JPEG, PNG, WebP - Multiple files supported
              </p>
            </div>
          </div>

          {/* Preview of uploaded files */}
          {cards.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                {cards.length} card{cards.length !== 1 ? 's' : ''} ready to scan
              </p>
              <div className="flex flex-wrap gap-2">
                {cards.map((card) => (
                  <img
                    key={card.id}
                    src={card.thumbnailUrl}
                    alt="Business card"
                    className="w-24 h-16 object-cover rounded border"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Start button */}
          <button
            type="button"
            onClick={handleStartProcessing}
            disabled={cards.length === 0 || !sessionSalesRepId}
            className="btn btn-primary"
          >
            Start Processing ({cards.length} card{cards.length !== 1 ? 's' : ''})
          </button>
        </div>
      )}

      {/* Step 2: Review */}
      {step === 'review' && (
        <div className="space-y-4">
          {/* Progress bar */}
          {(isProcessing || pendingCount > 0 || processingCount > 0) && (
            <div className="card">
              <div className="flex items-center gap-3">
                <Loader2 className="animate-spin text-blue-600" size={18} />
                <span className="text-sm text-gray-700">
                  Processing... {parsedCount + approvedCount} of {cards.length} cards scanned
                </span>
              </div>
              <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${((parsedCount + approvedCount) / cards.length) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Bulk actions */}
          {parsedCount > 0 && (
            <div className="flex gap-3">
              <button type="button" onClick={approveAll} className="btn btn-primary text-sm">
                Approve All ({parsedCount})
              </button>
            </div>
          )}

          {/* Card list */}
          <div className="space-y-3">
            {cards.map((card) => (
              <BusinessCardReviewCard
                key={card.id}
                card={card}
                companyTypes={companyTypes}
                onUpdateField={updateCardData}
                onApprove={approveCard}
                onSkip={skipCard}
                onSetAction={setImportAction}
              />
            ))}
          </div>

          {/* Import button */}
          {!isProcessing && pendingCount === 0 && processingCount === 0 && (
            <div className="card flex items-center justify-between">
              <p className="text-sm text-gray-600">
                {approvedCount} approved, {cards.filter((c) => c.status === 'skipped').length} skipped,{' '}
                {cards.filter((c) => c.status === 'error').length} errors
              </p>
              <button
                type="button"
                onClick={handleImport}
                disabled={approvedCount === 0}
                className="btn btn-primary"
              >
                Import {approvedCount} Card{approvedCount !== 1 ? 's' : ''}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Importing */}
      {step === 'importing' && (
        <div className="card text-center py-12">
          <Loader2 className="animate-spin text-blue-600 mx-auto" size={40} />
          <p className="text-gray-700 mt-4 font-medium">Importing companies and contacts...</p>
          <p className="text-sm text-gray-500 mt-1">Please don't close this page.</p>
        </div>
      )}

      {/* Step 4: Complete */}
      {step === 'complete' && importResult && (
        <div className="card text-center py-8 space-y-4">
          <CheckCircle2 size={48} className="text-green-500 mx-auto" />
          <h2 className="text-lg font-bold text-gray-900">Import Complete</h2>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-lg mx-auto">
            <StatCard label="Companies Created" value={importResult.companiesCreated} color="green" />
            <StatCard label="Contacts Added" value={importResult.contactsAdded} color="blue" />
            <StatCard label="Skipped" value={importResult.skipped} color="gray" />
            <StatCard label="Errors" value={importResult.errors} color={importResult.errors > 0 ? 'red' : 'gray'} />
          </div>

          <div className="flex gap-3 justify-center pt-4">
            <button
              type="button"
              onClick={() => navigate('/companies')}
              className="btn btn-primary"
            >
              View Companies
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="btn btn-secondary flex items-center gap-2"
            >
              <RotateCcw size={16} />
              Import More
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ===========================================
// Sub-components
// ===========================================

function StepBadge({ label, active, done }: { label: string; active: boolean; done: boolean }) {
  return (
    <span
      className={`px-2.5 py-1 rounded-full text-xs font-medium ${
        active
          ? 'bg-blue-100 text-blue-700'
          : done
          ? 'bg-green-100 text-green-700'
          : 'bg-gray-100 text-gray-500'
      }`}
    >
      {label}
    </span>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  const colorMap: Record<string, string> = {
    green: 'text-green-600',
    blue: 'text-blue-600',
    red: 'text-red-600',
    gray: 'text-gray-600',
  };
  return (
    <div className="text-center">
      <p className={`text-2xl font-bold ${colorMap[color] || colorMap.gray}`}>{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
}
